import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash } from '@phosphor-icons/react';
import { api } from '../../lib/api';
import { SkeletonAdminList } from '../../components/Skeleton';
import { useConfirm } from '../../components/ConfirmModal';

function getUserTypeLabel(userType) {
  if (userType === 'super_admin') return 'Super Admin';
  if (userType === 'admin') return 'Clinic Admin';
  return '';
}

function InviteUserModal({ onClose, onInvited }) {
  const [clinics, setClinics] = useState([]);
  const [loadingClinics, setLoadingClinics] = useState(true);
  const [clinicId, setClinicId] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.admin.clinics()
      .then((data) => {
        setClinics(data);
        if (data.length > 0) setClinicId(data[0].id);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoadingClinics(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!clinicId) {
      setError('Please select a clinic');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await api.settings.invite(clinicId, {
        email,
        first_name: firstName,
        last_name: lastName,
      });
      onInvited();
      onClose();
    } catch (err) {
      setError(err.message);
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4" onClick={onClose}>
      <div className="bg-white rounded-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-text mb-4">Invite User</h3>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-text mb-1">First Name</label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              className="w-full border border-[#E6E6E6] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-30"
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-text mb-1">Last Name</label>
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              className="w-full border border-[#E6E6E6] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-30"
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-text mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full border border-[#E6E6E6] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-30"
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-text mb-1">Clinic</label>
            <select
              value={clinicId}
              onChange={(e) => setClinicId(e.target.value)}
              required
              disabled={loadingClinics}
              className="w-full border border-[#E6E6E6] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-30 bg-white disabled:opacity-50"
            >
              {loadingClinics ? (
                <option>Loading clinics...</option>
              ) : clinics.length === 0 ? (
                <option value="">No clinics available</option>
              ) : (
                clinics.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))
              )}
            </select>
          </div>

          {error && (
            <div className="mb-3 p-2 bg-destructive-10 border border-destructive-30 rounded-lg text-sm text-destructive">
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-primary border border-primary-30 rounded-lg hover:bg-[#F5E9FD]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || loadingClinics || clinics.length === 0}
              className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-80 disabled:opacity-50"
            >
              {submitting ? 'Sending...' : 'Send Invite'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const { confirm, confirmModal } = useConfirm();

  const loadUsers = () => {
    setLoading(true);
    Promise.all([api.admin.users(), api.admin.invites()])
      .then(([u, i]) => { setUsers(u); setInvites(i); })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  const handleDeleteInvite = async (email) => {
    const ok = await confirm({
      title: 'Delete pending invite?',
      message: email ? `The invite for ${email} will be cancelled. They won't be able to use the link from their email.` : 'The pending invite will be cancelled.',
      confirmLabel: 'Delete invite',
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.admin.deleteInviteByEmail(email);
      loadUsers();
    } catch (err) {
      console.error('Delete invite failed:', err);
    }
  };

  const handleImpersonate = async (userId) => {
    setImpersonating(userId);
    try {
      const { url } = await api.admin.impersonateUser(userId);
      window.open(url, '_blank');
    } catch (err) {
      console.error('Impersonate failed:', err);
    }
    setImpersonating(null);
  };

  useEffect(() => {
    loadUsers();
  }, []);

  return (
    <div>
      <div className="flex items-start sm:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl md:text-[32px] font-semibold text-text mb-1">Users</h1>
          <p className="text-sm text-gray-500">This is a list of all users in the database</p>
        </div>
        <button
          onClick={() => setInviteOpen(true)}
          className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-80 transition-colors flex items-center gap-1 flex-shrink-0"
        >
          <span className="text-lg leading-none">+</span> Invite User
        </button>
      </div>

      {loading ? (
        <SkeletonAdminList rows={6} />
      ) : (
        <div className="bg-white rounded-xl border border-[#E6E6E6]">
          {invites.map((inv) => {
            const initials = `${inv.first_name?.[0] || ''}${inv.last_name?.[0] || ''}`.toUpperCase() || '?';
            return (
              <div
                key={`invite-${inv.id}`}
                className="px-4 md:px-5 py-3.5 border-b border-[#E6E6E6] bg-gray-50/50"
              >
                {/* Desktop row */}
                <div className="hidden md:flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 bg-gray-300">
                      <span className="text-white text-[13px] font-semibold">{initials}</span>
                    </div>
                    <div className="w-[200px] min-w-0">
                      <p className="text-sm font-medium text-text truncate">
                        {inv.first_name} {inv.last_name}
                      </p>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-500 truncate">{inv.email}</p>
                    </div>
                    <div className="w-[120px]">
                      <span className="text-xs font-medium text-gray-600 bg-gray-200 px-2 py-1 rounded">
                        Invited
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteInvite(inv.email)}
                    className="text-gray-400 hover:text-destructive transition-colors p-1 ml-4 flex-shrink-0"
                    title="Delete invite"
                  >
                    <Trash size={18} />
                  </button>
                </div>

                {/* Mobile row */}
                <div className="flex md:hidden items-center gap-3">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 bg-gray-300">
                    <span className="text-white text-[13px] font-semibold">{initials}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-text truncate">
                        {inv.first_name} {inv.last_name}
                      </p>
                      <span className="text-[10px] font-medium text-gray-600 bg-gray-200 px-1.5 py-0.5 rounded flex-shrink-0">
                        Invited
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 truncate">{inv.email}</p>
                  </div>
                  <button
                    onClick={() => handleDeleteInvite(inv.email)}
                    className="text-gray-400 hover:text-destructive transition-colors p-1 flex-shrink-0"
                    title="Delete invite"
                  >
                    <Trash size={18} />
                  </button>
                </div>
              </div>
            );
          })}
          {users.map((user, idx) => {
            const initials = `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase();
            const typeLabel = getUserTypeLabel(user.user_type);

            return (
              <div
                key={user.id}
                className={`px-4 md:px-5 py-3.5 cursor-pointer hover:bg-gray-50 transition-colors ${
                  idx !== users.length - 1 ? 'border-b border-[#E6E6E6]' : ''
                }`}
                onClick={() => navigate(`/admin/users/${user.id}`)}
              >
                {/* Desktop row */}
                <div className="hidden md:flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: '#7F31DB' }}
                    >
                      <span className="text-white text-[13px] font-semibold">{initials}</span>
                    </div>
                    <div className="w-[200px] min-w-0">
                      <p className="text-sm font-medium text-text truncate">
                        {user.first_name} {user.last_name}
                      </p>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-500 truncate">{user.email}</p>
                    </div>
                    <div className="w-[120px]">
                      {typeLabel && (
                        <span className="text-xs font-medium text-primary bg-[#F5E9FD] px-2 py-1 rounded">
                          {typeLabel}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleImpersonate(user.id); }}
                    disabled={impersonating === user.id}
                    className="px-3 py-1.5 text-sm font-medium text-primary border border-primary-30 rounded-lg hover:bg-[#F5E9FD] transition-colors flex-shrink-0 ml-4"
                  >
                    {impersonating === user.id ? 'Loading...' : 'Log in as'}
                  </button>
                </div>

                {/* Mobile row */}
                <div className="flex md:hidden items-center gap-3">
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: '#7F31DB' }}
                  >
                    <span className="text-white text-[13px] font-semibold">{initials}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-text truncate">
                        {user.first_name} {user.last_name}
                      </p>
                      {typeLabel && (
                        <span className="text-[10px] font-medium text-primary bg-[#F5E9FD] px-1.5 py-0.5 rounded flex-shrink-0">
                          {typeLabel}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 truncate">{user.email}</p>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleImpersonate(user.id); }}
                    disabled={impersonating === user.id}
                    className="px-2.5 py-1.5 text-xs font-medium text-primary border border-primary-30 rounded-lg hover:bg-[#F5E9FD] transition-colors flex-shrink-0"
                  >
                    {impersonating === user.id ? 'Loading...' : 'Log in as'}
                  </button>
                </div>
              </div>
            );
          })}
          {users.length === 0 && invites.length === 0 && (
            <div className="px-5 py-8 text-center text-sm text-gray-500">No users found</div>
          )}
        </div>
      )}

      {inviteOpen && (
        <InviteUserModal
          onClose={() => setInviteOpen(false)}
          onInvited={loadUsers}
        />
      )}
      {confirmModal}
    </div>
  );
}
